// ──────────────────────────────────────────────────────────────────────────
//  Engine — TypeScript port of Engine.lua. Pure functions, no React.
//  P(roll hits a BiS upgrade) = hits / poolSize for each (pool, spec) pair.
// ──────────────────────────────────────────────────────────────────────────

import type {
  CharState,
  EquippedMythState,
  GroupID,
  HitItem,
  Item,
  LootCache,
  OffTargetItem,
  Pool,
  PoolSpecBreakdown,
  Recommendation,
  SkippedItem,
} from "./types";
import { groupForSlot, isCatalyzableGroup, GROUP_BY_ID, BIS_SLOTS } from "./bis-slots";
import { CLASS_BY_ID } from "./classes";

/** Per-item verdict — used by computeRecommendations to bucket each
 *  pool item into hits / auto-skipped / off-target. The bonus-roll
 *  pool's poolSize (denominator of P%) counts items in any of the
 *  three buckets; only `hit` items count toward the numerator.
 *
 *    hit         → user wants it AND would upgrade. Best outcome.
 *                  `isPreferred` distinguishes BiS-listed / catalyzed
 *                  hits (= user's wishlist) from "any-upgrade-counts"
 *                  hits (= slot below Myth, item not on the list).
 *    skipped     → user wants it BUT the engine knows it won't upgrade
 *                  (already owned, slot already at Myth, marked rolled).
 *    off-target  → not in the user's BiS config (slot mode is `skip`
 *                  or specific list doesn't include this item). Bonus
 *                  roll on this = wasted currency just like a skip. */
type HitVerdict =
  | { kind: "hit"; isPreferred: boolean }
  | { kind: "skipped"; reason: SkippedItem["reason"] }
  | { kind: "off-target"; reason: OffTargetItem["reason"] };

/** Per-item verdict against current BiS targets + Myth state. */
export function itemHitVerdict(
  item: Item,
  bis: CharState["bis"][number] | undefined,
  myth: EquippedMythState,
): HitVerdict {
  if (!bis) return { kind: "off-target", reason: "not-in-bis" };
  const groupID = groupForSlot(item.slot);
  if (!groupID) return { kind: "off-target", reason: "not-in-bis" };
  const target = bis[groupID];
  if (!target || target.mode === "skip") {
    return { kind: "off-target", reason: "slot-skipped" };
  }

  if (target.mode === "specific") {
    const ids = target.itemIDs ?? [];
    const inBisList = ids.length > 0 && ids.includes(item.id);

    if (inBisList) {
      // The item IS on the user's preferred BiS list — best case.
      // Already at Myth in their inventory → wasted bonus roll.
      if (myth.mythItemIDs.includes(item.id)) {
        return { kind: "skipped", reason: "already-owned" };
      }
      return { kind: "hit", isPreferred: true };
    }

    // Item NOT on the user's BiS list. Two cases:
    //
    //   a) Slot is already at Myth → bonus roll for a non-BiS item
    //      would replace a Myth piece with a non-BiS Myth piece. No
    //      stat / track upgrade — off-target (wasted roll).
    //
    //   b) Slot is NOT at Myth → ANY Myth-track drop in this slot is
    //      a real track upgrade over what the user's wearing, even
    //      though they didn't explicitly list it. Counts as a hit
    //      with isPreferred=false (the user didn't ask for THIS item,
    //      it's just an acceptable side-grade).
    if (!myth.satisfiedGroups[groupID]) {
      return { kind: "hit", isPreferred: false };
    }
    return { kind: "off-target", reason: "not-in-bis" };
  }

  if (target.mode === "catalyzed") {
    // Catalyzed mode on a non-catalyzable slot (RING / TRINKET /
    // MAINHAND / OFFHAND / NECK) is a misconfiguration the BiS UI
    // should prevent — we treat it as off-target rather than hit
    // since rolling for these items has no catalyst path to BiS.
    if (!isCatalyzableGroup(groupID)) {
      return { kind: "off-target", reason: "not-in-bis" };
    }
    if (myth.satisfiedGroups[groupID]) {
      return { kind: "skipped", reason: "slot-at-myth" };
    }
    // Catalyzed = the user's stated preference is "any catalyzable
    // item in this slot" — every catalyzed hit IS preferred BiS.
    return { kind: "hit", isPreferred: true };
  }
  return { kind: "off-target", reason: "not-in-bis" };
}

/** Legacy boolean wrapper. New code should use `itemHitVerdict`. */
export function itemIsHit(
  item: Item,
  bis: CharState["bis"][number] | undefined,
  myth: EquippedMythState,
): boolean {
  return itemHitVerdict(item, bis, myth).kind === "hit";
}

interface ComputeArgs {
  cache: LootCache;
  state: CharState;
}

/**
 * Per-(pool × spec) recommendations, ranked by P. Mirrors Engine.lua's
 * ComputeRecommendations: pick the BEST spec row per pool (P, then hits,
 * then played-spec preference), then sort the surviving rows.
 */
export function computeRecommendations({ cache, state }: ComputeArgs): Recommendation[] {
  const bis = state.bis[state.playedSpecID];
  if (!bis) return [];

  // Need at least one non-skip target.
  const hasTarget = Object.values(bis).some((t) => t && t.mode !== "skip");
  if (!hasTarget) return [];

  const myth = state.equippedMyth;
  const filter = state.contentFilter;
  const playedID = state.playedSpecID;
  const classDef = CLASS_BY_ID[state.classID];
  if (!classDef) return [];
  const classSpecs = classDef.specs;

  const bestByPool = new Map<string, Recommendation>();

  const isBetter = (cand: Recommendation, cur: Recommendation | undefined): boolean => {
    if (!cur) return true;
    if (cand.p !== cur.p) return cand.p > cur.p;
    if (cand.hits !== cur.hits) return cand.hits > cur.hits;
    // BiS-preferred hits tiebreak hits — when two specs of the same
    // pool tie on probability and hit count, the spec that lines up
    // with more of the user's ACTUAL wishlist (catalyzed slots +
    // specific BiS items) ranks higher.
    if (cand.bisHits !== cur.bisHits) return cand.bisHits > cur.bisHits;
    if (cand.specID === playedID && cur.specID !== playedID) return true;
    return false;
  };

  for (const pool of Object.values(cache.pools)) {
    const allowed =
      filter === "both" ||
      (filter === "mplus" && !pool.isRaid) ||
      (filter === "raid" && pool.isRaid);
    if (!allowed) continue;

    for (const spec of classSpecs) {
      let poolSize = 0;
      let hits = 0;
      let bisHits = 0;
      const hitItems: HitItem[] = [];
      const skippedItems: SkippedItem[] = [];
      const offTargetItems: OffTargetItem[] = [];
      for (const itemID of pool.itemOrder) {
        const item = cache.items[itemID];
        if (!item) continue;
        if (!item.eligibleSpecs.includes(spec.id)) continue;
        // Manually-marked-rolled items: surface as a skip-reason
        // alongside the hit/skip-due-to-myth set, but DON'T count
        // toward poolSize (they're truly excluded from the roll).
        if (state.exclusions[pool.id]?.[itemID]) {
          // Still annotate so the user sees "you've already rolled this"
          // when reviewing why a pool's number is what it is.
          const verdict = itemHitVerdict(item, bis, myth);
          if (verdict.kind === "hit" || verdict.kind === "skipped") {
            skippedItems.push({ item, reason: "rolled" });
          }
          continue;
        }
        poolSize += 1;
        const verdict = itemHitVerdict(item, bis, myth);
        if (verdict.kind === "hit") {
          hits += 1;
          if (verdict.isPreferred) bisHits += 1;
          hitItems.push({ item, isPreferred: verdict.isPreferred });
        } else if (verdict.kind === "skipped") {
          skippedItems.push({ item, reason: verdict.reason });
        } else {
          // off-target — eligible for spec, in pool, but won't help
          offTargetItems.push({ item, reason: verdict.reason });
        }
      }
      // Only surface pools where we'd actually recommend rolling
      // (hits > 0). Pools with only skipped/off-target items would
      // be 0% recommendations — noise. The skipped + off-target lists
      // still ride along on the kept rows so the UI can explain
      // every item the bonus roll could land on.
      if (poolSize > 0 && hits > 0) {
        const row: Recommendation = {
          pool, specID: spec.id, specName: spec.name,
          poolSize, hits, bisHits, p: hits / poolSize,
          hitItems, skippedItems, offTargetItems,
        };
        if (isBetter(row, bestByPool.get(pool.id))) {
          bestByPool.set(pool.id, row);
        }
      }
    }
  }

  const rows = Array.from(bestByPool.values());
  rows.sort((a, b) => {
    if (a.p !== b.p) return b.p - a.p;
    if (a.hits !== b.hits) return b.hits - a.hits;
    // bisHits tiebreaker — pools where more of the hit items are
    // BiS-preferred rank above pools with same total but more
    // any-upgrade hits.
    if (a.bisHits !== b.bisHits) return b.bisHits - a.bisHits;
    return (a.pool.label || "").localeCompare(b.pool.label || "");
  });
  return rows;
}

/** Per-spec P% breakdown for a single pool. Powers the Pools tab. */
export function poolBreakdown(
  poolID: string,
  cache: LootCache,
  state: CharState,
): PoolSpecBreakdown[] {
  const pool = cache.pools[poolID];
  if (!pool) return [];
  const bis = state.bis[state.playedSpecID];
  if (!bis) return [];
  const classDef = CLASS_BY_ID[state.classID];
  if (!classDef) return [];

  const myth = state.equippedMyth;
  const out: PoolSpecBreakdown[] = [];
  for (const spec of classDef.specs) {
    let poolSize = 0;
    let hits = 0;
    for (const itemID of pool.itemOrder) {
      const item = cache.items[itemID];
      if (!item) continue;
      if (!item.eligibleSpecs.includes(spec.id)) continue;
      if (state.exclusions[poolID]?.[itemID]) continue;
      poolSize += 1;
      if (itemIsHit(item, bis, myth)) hits += 1;
    }
    out.push({
      specID: spec.id,
      specName: spec.name,
      poolSize,
      hits,
      p: poolSize > 0 ? hits / poolSize : 0,
    });
  }
  return out;
}

/** All BiS group IDs ordered by their declaration in BIS_SLOTS. */
export const ALL_GROUP_IDS: GroupID[] = BIS_SLOTS.map((g) => g.id);

/** Group "satisfied" requires N=positions slots equipped on Myth. */
export function groupRequiredPositions(groupID: GroupID): number {
  return GROUP_BY_ID[groupID]?.positions ?? 1;
}

/** Build a recommendation summary string (e.g. "8 pools · 3 with P ≥ 25%"). */
export function summarizeRecommendations(rows: Recommendation[]): string {
  if (rows.length === 0) return "No recommendations — configure your BiS first.";
  const high = rows.filter((r) => r.p >= 0.25).length;
  const mid = rows.filter((r) => r.p >= 0.12 && r.p < 0.25).length;
  return `${rows.length} pools · ${high} high · ${mid} medium`;
}
